import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import i18next from "src/i18n/config";
import { useCountryName } from "src/ui/hooks/useCountryName";

import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { getSettings } from "@services/configService";
import { DangerOutlinedButton, PrimaryOutlinedButton } from "@shared/CustomButton";
import { SectionField, SectionFields } from "@shared/SectionPanel/SectionField";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";

import { useAccountProfile } from "../../hooks/useAccountProfile";

import { DeleteAccountForm } from "./DeleteAccountForm";
import { ProfileEditForm } from "./ProfileEditForm";

function displayValue(value: string | null | undefined) {
    return value?.trim() ? value : "-";
}

function capitalize(value: string | null | undefined) {
    if (!value?.trim()) {
        return "-";
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function AccountProfilePage() {
    const { t } = useTranslation(["common", "pages"]);
    const { profile, isLoading, refetch } = useAccountProfile();
    const countryName = useCountryName(profile?.country);
    const [searchParams, setSearchParams] = useSearchParams();
    const mode = searchParams.get("mode");
    const { marketingEnabled } = getSettings().features;

    const clearMode = useCallback(() => setSearchParams({}, { replace: true }), [setSearchParams]);
    const handleProfileEditSuccess = useCallback(() => {
        clearMode();
        void refetch();
    }, [clearMode, refetch]);

    if (!isLoading && !profile) {
        return null;
    }

    return (
        <PageContainer fullWidth={false} isLoading={isLoading}>
            {mode === "edit" && profile ? (
                <ProfileEditForm profile={profile} onSuccess={handleProfileEditSuccess} onCancel={clearMode} />
            ) : mode === "delete" ? (
                <DeleteAccountForm onCancel={clearMode} />
            ) : (
                <SectionPanel
                    title={t("pages:settingsPage.profileData.title")}
                    actions={
                        <>
                            <Link to="?mode=edit">
                                <PrimaryOutlinedButton
                                    size="small"
                                    startIcon={<i className="fa-solid fa-fw fa-pencil" />}
                                    name="edit-profile"
                                >
                                    {t("common:button.edit")}
                                </PrimaryOutlinedButton>
                            </Link>
                            <Link to="?mode=delete">
                                <DangerOutlinedButton
                                    size="small"
                                    startIcon={<i className="fa-solid fa-fw fa-trash" />}
                                    name="delete-profile"
                                >
                                    {t("pages:settingsPage.profileData.delete")}
                                </DangerOutlinedButton>
                            </Link>
                        </>
                    }
                >
                    <SectionFields>
                        <SectionField label={t("pages:settingsPage.profileData.email")} value={displayValue(profile?.email)} />
                        <SectionField
                            label={t("pages:settingsPage.profileData.authentication")}
                            value={
                                profile?.twoFactorEnabled
                                    ? t("pages:settingsPage.profileData.twoFactor")
                                    : t("common:label.password")
                            }
                        />
                        <SectionField label={t("pages:settingsPage.profileData.name")} value={displayValue(profile?.name)} />
                        <SectionField
                            label={t("pages:settingsPage.profileData.phone")}
                            value={displayValue(`${profile?.phoneCode ?? ""}${profile?.phoneNumber ?? ""}`)}
                        />
                        <SectionField label={t("common:label.address")} value={displayValue(profile?.address)} />
                        <SectionField label={t("pages:settingsPage.profileData.city")} value={capitalize(profile?.city)} />
                        <SectionField
                            label={t("pages:settingsPage.profileData.zipCode")}
                            value={displayValue(profile?.zipCode)}
                        />
                        <SectionField label={t("pages:settingsPage.profileData.country")} value={displayValue(countryName)} />
                        {marketingEnabled ? (
                            <>
                                <SectionField
                                    label={t("pages:settingsPage.profileData.notifications")}
                                    value={
                                        profile?.newsletterChecked
                                            ? t("pages:settingsPage.profileData.extended")
                                            : t("pages:settingsPage.profileData.standard")
                                    }
                                />
                                <SectionField
                                    label={t("pages:settingsPage.profileData.usage")}
                                    value={
                                        profile?.typeOfUsage === "b"
                                            ? t("pages:settingsPage.profileData.businessUse")
                                            : t("pages:settingsPage.profileData.nonBusinessUse")
                                    }
                                />
                                <SectionField
                                    label={t("pages:settingsPage.profileData.company")}
                                    value={displayValue(profile?.companyName)}
                                />
                                <SectionField
                                    label={t("pages:settingsPage.profileData.industry")}
                                    value={displayValue(profile?.fieldOfApplication)}
                                />
                            </>
                        ) : null}
                    </SectionFields>
                </SectionPanel>
            )}
        </PageContainer>
    );
}

export default withMetaDescription(AccountProfilePage, {
    title: i18next.t("pages:settingsPage.pages.profile.metaTitle"),
});
